import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { toast } from 'react-toastify';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  TextField,
  TableBody,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  Button,
  Typography,
  FormHelperText,
  Box,
} from '@mui/material';
import usersApi from '@api/users.api';
import Modal from '@components/Modal';
import Loader from '@components/Loader';
import { validationMap } from './adminUsers.helper';
import { StyledName } from './styles';

const defaultUser = {
  password: '',
  username: '',
  roles: [],
};

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(defaultUser);
  const [validate, setValidate] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const data = await await usersApi.fetch();
      await setUsers(data);
      setLoading(false);
    } catch (err) {
      toast.error(err?.message)
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async (row) => {
    setUser(row);
    setOpenModal(true);
  };

  const handleChange = (obj) => {
    setUser({ ...user, ...obj });
  };

  const validator = (key: string, passValidate = false): string => {
    if ((validate || passValidate) && validationMap[key]?.isError(user[key])) {
      return validationMap[key]?.helperText;
    }
    return '';
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleSubmit = async () => {
    setValidate(true);

    if (
      !validator('username', true) &&
      !validator('password', true) &&
      !validator('roles', true)
    ) {
      setValidate(false);
      if (user?._id) {
        await usersApi.edit(user, user._id);
      } else {
        await usersApi.add(user);
      }
      fetchList();
      setOpenModal(false);
    }
  };

  return (
    <div>
      <Box
        sx={{
          textAlign: 'right',
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h2" component="h2">
          Users
        </Typography>
        <Button
          sx={{ pl: 1, pr: 1 }}
          variant="contained"
          onClick={() => handleClick({})}
          size="large"
        >
          Benutzer hinzufügen
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Updated At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((row) => (
              <TableRow
                key={row._id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell>
                  <StyledName onClick={() => handleClick(row)}>
                    {row.username}
                  </StyledName>
                </TableCell>
                <TableCell>{row.roles.join(',')}</TableCell>
                <TableCell>{row.createdAt}</TableCell>
                <TableCell>{row.updatedAt}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal
        onConfirm={() => handleSubmit()}
        title={user?._id ? `Edit ${user.username}` : 'New User'}
        modalOpen={openModal}
        onClose={() => setOpenModal(false)}
      >
        <TextField
          sx={{ width: '100%', mb: 1 }}
          label="Username"
          error={!!validator('username')}
          helperText={validator('username')}
          defaultValue={user?.username || ''}
          variant="standard"
          onChange={(e) => handleChange({ username: e.target.value })}
        />

        <FormControl
          variant="standard"
          sx={{ mb: 1, width: '100%' }}
          error={!!validator('roles')}
        >
          <InputLabel>Type</InputLabel>
          <Select
            value={user?.roles ? user.roles[0] : ''}
            onChange={(e) => handleChange({ roles: [e.target.value] })}
            label="Type"
          >
            <MenuItem value="Admin">Admin</MenuItem>
            <MenuItem value="User">User</MenuItem>
          </Select>
          <FormHelperText>{validator('roles')}</FormHelperText>
        </FormControl>

        <TextField
          sx={{ width: '100%' }}
          label="Password"
          error={!!validator('password')}
          helperText={validator('password')}
          defaultValue={user?.password || ''}
          variant="standard"
          onChange={(e) => handleChange({ password: e.target.value })}
        />
      </Modal>

      {loading && <Loader filled />}
    </div>
  );
};

export default observer(AdminUsers);
